import React, { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import theme from '@app/ui/theme'
import { Image } from '@ui/blocks/Image'
import SubLinks from '@ui/business-components/Header/TopMenu/SubLinks'
import SubSection from '@ui/business-components/Header/TopMenu/SubSection'
import MobileLayout from '@ui/business-components/Header/TopMenu/MobileLayout'
import chevron from '@app/ui/assets/images/header/menu-chevron.svg'
import { useLockBodyScroll } from '@app/hooks/useLockBodyScroll'
import { PhoneLink } from '../PhoneLink'

import { navData } from './data'

interface TopMenuData {
  id: number
  text: string
  path: string
  hasChild: boolean
}

interface TopMenuProps {
  data?: TopMenuData[]
  isMenuSticky: boolean
  onToggleMobileOpen: () => void
  isMobileOpen: boolean
}

interface MenuLinkProps {
  $opened?: boolean | undefined
  $isMenuSticky?: boolean
}

const Navigation = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  flex-shrink: 0;
`
const DesktopMenu = styled(Navigation)`
  @media ${({ theme }) => theme.device.laptop} {
    display: none;
  }
`
const MobileMenu = styled(Navigation)`
  display: none;
  @media ${({ theme }) => theme.device.laptop} {
    display: flex;
  }
`

export const MenuLink = styled(Link)<MenuLinkProps>`
  color: ${({ theme }) => theme.global.colors.elemyGreen};
  font-style: normal;
  font-weight: normal;
  font-size: 1.5rem;
  line-height: 1.8rem;
  text-transform: capitalize;
  margin: 0 0.9rem;
  display: flex;
  align-items: center;
  transition: all 0.4s;
  padding: 1.3rem 1.9rem;
  border-radius: 100rem;
  background-color: ${({ $opened, theme, $isMenuSticky }) => {
    if ($opened) {
      return $isMenuSticky ? theme.global.colors.ecru : theme.global.colors.warmOrange
    }
    return 'transparent'
  }};

  :hover {
    color: ${({ theme }) => `${theme.global.colors.genoa}`};
  }

  :active {
    color: ${({ theme }) => `${theme.global.colors.elemyGreen}`};
  }

  &:last-of-type {
    margin-right: 0;
  }

  @media ${theme.device.laptopL} {
    margin: 0 0.2rem;
    padding: 1.3rem 1rem;
  }

  @media ${theme.device.laptop} {
    padding: 0;
  }

  &:first-of-type {
    margin-left: 0;
  }

  span {
    margin-left: 0.8rem;
    img {
      position: relative;
      top: 0.1rem;
      transition: all 0.2s;
      transform-origin: center center;
      transform: ${({ $opened }) => ($opened ? 'rotate(180deg)' : 'rotate(0deg)')};
    }
  }
`

export const MenuLinkLogin = styled(MenuLink)`
  &&& {
    margin: 0 1.6rem 0 0;
  }
`

const BurgerMenuButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1rem 0 1rem 1rem;
  cursor: pointer;
  width: 3rem;
  height: 4rem;
  margin-left: 2.2rem;

  @media ${theme.device.mobileL} {
    margin-left: 1rem;
  }
`

const BurgerLine = styled.div<{ isMobileOpen: boolean }>`
  border: 0.1rem solid ${({ theme }) => theme.global.colors.elemyGreen};
  background-color: ${({ theme }) => theme.global.colors.elemyGreen};
  border-radius: 2rem;
  transition: 0.4s;

  &:first-child {
    margin-bottom: 1rem;

    ${({ isMobileOpen }) => isMobileOpen && 'transform: rotate(-45deg) translate(-5px,4.5px);'}
  }

  &:last-child {
    ${({ isMobileOpen }) => isMobileOpen && 'transform: rotate(45deg) translate(-4px,-4px);'}
  }
`

const TopMenu = ({ data = navData, isMenuSticky, onToggleMobileOpen, isMobileOpen }: TopMenuProps) => {
  const isMobile = useMediaQuery({ query: theme.device.mobileL })
  let menuTimeout: ReturnType<typeof setTimeout>

  const [openedItem, setOpenedItem] = useState(0)
  const [subListOpen, setSubListOpen] = useState(false)
  const [subSectionOpen, setSubSectionOpen] = useState(false)

  useEffect(() => {
    setSubSectionOpen(false)
    setSubListOpen(false)
    if (openedItem === 4) {
      setSubListOpen(true)
    } else if (openedItem === 1) {
      setSubSectionOpen(true)
    }
  }, [openedItem])

  useLockBodyScroll(isMobile && isMobileOpen)

  const showSubItems = (id: number) => {
    clearTimeout(menuTimeout)
    const item = data.find(item => id === item.id)
    if (item?.hasChild && openedItem !== item.id) {
      return setOpenedItem(item.id)
    }
  }

  const closeSubItems = () => {
    menuTimeout = setTimeout(() => setOpenedItem(0), 600)
  }

  const handleMouseEnter = () => {
    clearTimeout(menuTimeout)
  }

  return (
    <Navigation>
      <DesktopMenu>
        {data.map(({ id, text, path, hasChild }) => {
          return (
            <MenuLink
              $isMenuSticky={isMenuSticky}
              onMouseEnter={() => {
                showSubItems(id)
              }}
              onMouseLeave={closeSubItems}
              $opened={openedItem === id}
              key={id}
              to={path}>
              {text}
              {hasChild && (
                <span>
                  <Image width={10} height={10} blurOnLoad={false} src={chevron} alt="Menu chevron" />
                </span>
              )}
            </MenuLink>
          )
        })}
        <SubLinks mouseLeave={closeSubItems} mouseEnter={handleMouseEnter} isOpen={subListOpen} />
        <SubSection mouseLeave={closeSubItems} mouseEnter={handleMouseEnter} isOpen={subSectionOpen} />
      </DesktopMenu>

      <MobileMenu>
        <PhoneLink />
        <BurgerMenuButton onClick={onToggleMobileOpen}>
          <BurgerLine isMobileOpen={isMobileOpen} />
          <BurgerLine isMobileOpen={isMobileOpen} />
        </BurgerMenuButton>
        <MobileLayout topOffset={6.8} isOpen={isMobileOpen} />
      </MobileMenu>
    </Navigation>
  )
}

export default TopMenu
